/**
 * Pure helpers for the ReferenceImageInput component. F12c's StyleTransfer,
 * FaceSwap, TryOn, and friends each embed a ReferenceImageInput for "pick
 * an extra image"; the panel then forwards the produced SourceImageRef
 * into the request DTO (as `styleReferenceImageBase64` etc.).
 */

const MAX_REFERENCE_IMAGE_BYTES = 16 * 1024 * 1024; // 16 MiB — fal.ai cap

const IMAGE_MIME_PREFIX = 'image/';

const SUPPORTED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'avif'];

/**
 * Validate that a `File` looks like an image we can ship to the BE.
 * Returns a typed error string (or null on success) so the component can
 * render the failure reason inline.
 */
export function validateReferenceFile(file: File): string | null {
  if (!file.type.startsWith(IMAGE_MIME_PREFIX)) {
    return `Unsupported file type: ${file.type || 'unknown'}.`;
  }
  if (file.size > MAX_REFERENCE_IMAGE_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    return `File too large (${mb} MB). Maximum is 16 MB.`;
  }
  if (file.size <= 0) {
    return 'File is empty.';
  }
  return null;
}

/**
 * Convert a `File` to a `data:image/...;base64,...` string. The base64
 * form is what we ship as `sourceImageBase64` / `styleReferenceImageBase64`
 * etc. — the BE strips the prefix and forwards to the provider.
 *
 * Rejects when validation fails or FileReader throws.
 */
export function fileToDataUrl(file: File): Promise<string> {
  const validationError = validateReferenceFile(file);
  if (validationError) {
    return Promise.reject(new Error(validationError));
  }
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') resolve(result);
      else reject(new Error('Unexpected FileReader result type.'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed.'));
    reader.readAsDataURL(file);
  });
}

/**
 * Coarse validation that a string looks like a fetchable image URL.
 * Doesn't HEAD the URL or guarantee the response will be an image —
 * just filters out obvious garbage so the panel error message can be
 * specific.
 */
export function isLikelyImageUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://') && !trimmed.startsWith('data:image/')) {
    return false;
  }
  if (trimmed.startsWith('data:image/')) return true;
  try {
    const parsed = new URL(trimmed);
    const pathname = parsed.pathname.toLowerCase();
    const dot = pathname.lastIndexOf('.');
    if (dot < 0) {
      // No extension — could still be an image (e.g., a signed URL).
      // Accept; the panel surfaces a load failure if it isn't.
      return true;
    }
    const ext = pathname.slice(dot + 1);
    if (SUPPORTED_IMAGE_EXTENSIONS.includes(ext)) return true;
    // Unknown extension but a valid URL — accept; let the BE / panel
    // surface load failures.
    return true;
  } catch {
    return false;
  }
}

/**
 * Strip the `data:image/...;base64,` prefix from a data URL, returning the
 * raw base64 payload that the SSGP v3 request DTOs accept. Mirrors
 * `stripDataUrlPrefix` from maskMath but lives here so reference-handling
 * code doesn't need to import canvas-flavored utilities.
 */
export function stripDataUrlPrefix(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}
